import React from 'react';
import { BaseResourceListProps, SimpleList, SvgBin, SvgEdit } from '@app/components';
import { productApi } from '@app/store';
import { IProduct } from '@app/common';
import { theme } from '@app/theme';
import { createColumnHelper } from '@tanstack/react-table';
import { useTable } from '@app/hooks/useTable';
import { CheckPermissions } from '@app/components/CheckPermissions';

const columnHelper = createColumnHelper<IProduct>();

export const ProductList = ({ onUpdate, onDelete, onRowPress, selectedRowItem }: BaseResourceListProps<IProduct>) => {
  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Nom</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        const product = info.row.original;
        const imageUrl = product.images?.medium?.webp?.url || product.imageUrl;

        return (
          <SimpleList.ItemCol width="25%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemImage source={imageUrl} alt={info.getValue()} />
              <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
                {info.getValue()}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('productVariations', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Déclinaisons</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {info.getValue().length === 1 ? 'Unitaire' : info.getValue().length}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.accessor('tags', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Catégories</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText numberOfLines={2} active={info.active}>
            {info.getValue()?.map(tag => tag.name).join(', ')}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <SimpleList.HeaderItem width="25%" align='center'>
          <SimpleList.HeaderItemLabel>Actions</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemActions>

            <CheckPermissions permissions={['dashboard::products.update']}>
              <SimpleList.ItemAction onPress={() => onUpdate(row.original)}>
                <SvgEdit color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>

            <CheckPermissions permissions={['dashboard::products.delete']}>
              <SimpleList.ItemAction onPress={() => onDelete(row.original)}>
                <SvgBin color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>

          </SimpleList.ItemActions>
        </SimpleList.ItemCol>
      ),
    }),
  ];

  const { table, setSearch, isFetching } = useTable({
    columns,
    useQuery: ({ pagination, search }) => productApi.useReadManyProductsQuery({
      pagination,
      filters: {
        $or: [
          {
            _id: {
              $contains: search
            }
          },
          {
            name: {
              $contains: search
            }
          }
        ],
      },
      sort: ['name:ASC'],
      populate: [
        'tags',
        'productVariations',
        // 'catalogToProducts',
        'catalogToProducts.catalog'
      ]
    })
  });

  return (
    <SimpleList
      table={table}
      isFetching={isFetching}
      onSearch={setSearch}
      selectedRowItem={selectedRowItem}
      onRowPress={onRowPress} />
  )

};
